/**
 * Shape a conversation into the message array the Anthropic/Bedrock Messages API
 * will accept — and nothing else.
 *
 * SEAM: these helpers change when the PROVIDER'S MESSAGE-ARRAY RULES change
 * ("must start with a user message", "no blank text"), not when Meridian's
 * tool set, telemetry, or streaming options change. They are pure functions
 * over `{role, content}[]` with no provider client, no tool registry and no
 * config — which is why the request-validation path
 * (`api/handlers/message-handlers/bedrock-validator.ts`) can reuse them without
 * dragging the streaming stack in. Sanitizing the CONTENT of each message is a
 * different job and lives in `./sanitize-bedrock-messages`; this file only
 * decides which messages are present and in what order.
 *
 * Re-exported from `./bedrock-client` so every existing import path still works.
 */
import type { BedrockMessage } from './message-types.js';
import { logger } from '../logger.js';

/**
 * Bedrock conversation messages must start with a user message.
 * This type ensures type safety for message arrays passed to Bedrock.
 */
export type BedrockConversationMessages = readonly { role: string; content: unknown }[];

/**
 * Validates that a message array starts with a user message (required by Bedrock).
 * Returns true if valid, false otherwise.
 */
export function isValidBedrockConversation(messages: readonly { role: string; content: unknown }[]): boolean {
  if (!messages || messages.length === 0) {
    return false;
  }
  const first = messages[0] as { role: string };
  return first.role === 'user';
}

/**
 * Ensures a message array starts with a user message by finding the first user message.
 * For brand new chats with no messages, adds a placeholder user message.
 * This is critical for Bedrock API compatibility.
 */
export function ensureConversationStartsWithUser(
  messages: readonly { role: string; content: unknown }[]
): readonly { role: string; content: unknown }[] {
  if (!messages || messages.length === 0) {
    // For brand new chats, add a placeholder user message so Bedrock doesn't reject
    // Use '.' instead of '' to avoid "blank text" error from Bedrock
    return [{ role: 'user', content: '.' }];
  }
  
  const first = messages[0] as { role: string };
  if (first.role === 'user') {
    return messages;
  }

  const firstUserIndex = messages.findIndex((m: { role: string }) => m.role === 'user');
  
  if (firstUserIndex > 0) {
    // Start from first user message
    return messages.slice(firstUserIndex);
  }
  
  // No user message found, add placeholder at the beginning
  logger.warn('no user message found in conversation, adding placeholder', { op: 'ensureConversationStartsWithUser' });
  // Use '.' instead of '' to avoid "blank text" error from Bedrock
  return [{ role: 'user', content: '.' }, ...messages];
}

/**
 * Extract plain text from a Bedrock message content (string or array of blocks).
 */
function getTextFromContent(content: BedrockMessage['content']): string {
  if (typeof content === 'string') return content.trim();
  if (!Array.isArray(content)) return '';
  return content
    .map((b: { text?: string }) => (b && typeof b.text === 'string' ? b.text : ''))
    .filter(Boolean)
    .join('\n')
    .trim();
}

/**
 * Merge consecutive user messages into one so Bedrock sees cleaner user/assistant alternation.
 * Bedrock requires the first message to be user; it does not require strict alternation, but
 * multiple user messages in a row (e.g. after context-window slice) can be merged for clarity.
 */
export function mergeConsecutiveUserMessages(
  messages: readonly { role: string; content: unknown }[]
): BedrockMessage[] {
  if (!messages || messages.length <= 1) return [...(messages as BedrockMessage[])];
  const result: BedrockMessage[] = [];
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i] as BedrockMessage;
    if (msg.role !== 'user') {
      result.push(msg);
      i++;
      continue;
    }
    const texts: string[] = [getTextFromContent(msg.content)];
    let j = i + 1;
    while (j < messages.length && (messages[j] as BedrockMessage).role === 'user') {
      texts.push(getTextFromContent((messages[j] as BedrockMessage).content));
      j++;
    }
    const combined = texts.filter(Boolean).join('\n\n');
    result.push({
      role: 'user',
      content: combined || '.',
    });
    i = j;
  }
  return result;
}
